import { store } from '@kfc-global/react-shared/redux/Store';
import {
  getGeographicLocation,
  getCurrentAddress,
} from '@kfc-global/react-shared/redux/Actions/PlacesAutocompleteAction';

/*
 * Promise wrappers around the getGeographicLocation / getCurrentAddress actions
 * from @kfc-global/react-shared. The corresponding sagas
 * (placesGeolocationCall / getAddressCall) already handle AfriGis vs Google
 * routing based on tenantReducer.componentOptions.app.storeSearch.useAfriGis,
 * apply parseAddress + removePlusCode, and write the result into
 * placesAutocompleteReducer. These wrappers subscribe to the store, dispatch
 * the action, and resolve once the saga flips the matching loading flag back
 * to false — so callers can `await` them like regular async functions.
 */

const DEFAULT_TIMEOUT_MS = 15000;

const awaitStateTransition = ({
  loadingKey,
  resultKey,
  dispatchAction,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) =>
  new Promise((resolve, reject) => {
    let unsubscribe;
    let timeoutId;
    let sawLoading = false;
    let settled = false;

    const safeUnsubscribe = () => {
      try {
        if (typeof unsubscribe === 'function') unsubscribe();
      } catch (_) {
        // ignore
      }
    };

    const settleResolve = payload => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      safeUnsubscribe();
      resolve(payload);
    };

    const settleReject = error => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      safeUnsubscribe();
      reject(error);
    };

    const handleChange = () => {
      const reducer = store.getState()?.placesAutocompleteReducer || {};
      const isLoading = reducer?.[loadingKey];

      if (isLoading) {
        sawLoading = true;
        return;
      }

      // Only act once we have seen the loading flag turn true at least once
      if (!sawLoading) return;

      const results = reducer?.[resultKey] || [];
      const deliveryAddress = reducer?.deliveryAddress;
      const deliveryAddressPlaceId = reducer?.deliveryAddressPlaceId;
      // An empty results array is a valid "no matches" outcome, not a failure,
      // so treat any present array (even empty) as a success payload.
      const hasSuccessPayload =
        Array.isArray(results) || !!deliveryAddress || !!deliveryAddressPlaceId;

      // IMPORTANT:
      // Some sagas/reducers can leave a stale placesAutocompleteError even after success.
      // Only reject when there's an error AND we do not have a success payload.
      if (reducer?.placesAutocompleteError && !hasSuccessPayload) {
        settleReject(new Error('Places action failed'));
        return;
      }

      settleResolve({
        results: Array.isArray(results) ? results : [],
        deliveryAddress,
        deliveryAddressPlaceId,
      });
    };

    unsubscribe = store.subscribe(handleChange);
    timeoutId = setTimeout(() => {
      settleReject(new Error(`Places action timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    store.dispatch(dispatchAction);
  });

export const awaitGeographicLocation = payload =>
  awaitStateTransition({
    loadingKey: 'isPlacesGeoLoading',
    resultKey: 'geolocationResult',
    dispatchAction: getGeographicLocation(payload),
  });

export const awaitCurrentAddress = payload =>
  awaitStateTransition({
    loadingKey: 'isPlacesReverseGeoLoading',
    resultKey: 'response',
    dispatchAction: getCurrentAddress(payload),
  });
